import { useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AiCaptionServices } from "@/services/bedrock/AiCaptionServices";
import { QuoteEdit } from "@/services/bedrock/dtos/DesignRenderGroup";

export type ShareCaptionStatus = "none" | "pending" | "ready" | "failed";

export const shareCaptionKey = (playerId: number, reviewIds: number[]) =>
  ["shareCaption", playerId, reviewIds.join(",")] as const;

export interface ShareCaption {
  caption: string | null;
  status: ShareCaptionStatus;
  /** Asks for a new caption for the same reviews. */
  rewrite: () => void;
}

/**
 * The caption for a review post, written from the reviews alone so it can start the moment the
 * reviews are known, before any design is picked or rendered. Kept per review set, so switching
 * looks never rewrites it, and neither does a quote edit: the player's caption stays, and a
 * Rewrite is written from the edited quotes.
 */
export function useShareCaption(
  playerId: number | null,
  reviewIds: number[],
  enabled: boolean,
  quoteEdits: QuoteEdit[] = []
): ShareCaption {
  const queryClient = useQueryClient();
  const active = enabled && playerId != null && reviewIds.length > 0;
  const key = shareCaptionKey(playerId ?? -1, reviewIds);
  // Read when the caption is written, so an edit landing never changes the key and refetches.
  const editsRef = useRef(quoteEdits);
  editsRef.current = quoteEdits;

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const edits = editsRef.current;
      const request = {
        reviewIds,
        playerId: playerId as number,
        ...(edits.length ? { overrideValues: { reviews: edits } } : {}),
      };
      return (await AiCaptionServices.generate(request)).caption;
    },
    enabled: active,
    staleTime: Infinity,
    retry: false,
  });

  const rewrite = useCallback(() => {
    if (active) void queryClient.refetchQueries({ queryKey: key, exact: true });
  }, [active, queryClient, key.join("|")]);

  let status: ShareCaptionStatus = "none";
  if (active) {
    if (query.isFetching) status = "pending";
    else if (query.isError) status = "failed";
    else if (query.data) status = "ready";
    else status = "pending";
  }

  return { caption: query.data ?? null, status, rewrite };
}
